import { JSX } from 'react'

import { Signature } from 'lucide-react'

import { Tooltip } from '~/components/component-library'
import { DropdownMenuItem } from '~/components/component-library/Dropdown'
import { HideTokenDialogContent } from '~/components/smart-accounts/HideTokenDialogContent'
import { SupportedChainId } from '~/constants/chains'
import { useAuth } from '~/context/AuthProvider'
import { AssetType, IAddress } from '~/types'
import { EvmAddress } from '~/utils/address'
import { plural } from '~/utils/display'
import { isNativeTokenAddress } from '~/utils/tokens'

type HistoryItemProps = {
  title: string
  subtitle?: JSX.Element | string
  completed?: boolean
  first?: boolean
  last?: boolean
}

export const HistoryItem = ({
  title,
  subtitle,
  completed,
  first,
  last,
}: HistoryItemProps) => {
  return (
    <div className="relative flex items-start space-x-2 overflow-hidden py-2">
      {completed ? (
        <div className="z-10 mt-1 h-3 w-3 rounded-full bg-blue-500" />
      ) : (
        <div className="z-10 mt-1 h-3 w-3 rounded-full border border-gray-500 bg-surface" />
      )}
      <div>
        <div className="text-sm">{title}</div>
        {subtitle && <div className="text-xs text-secondary">{subtitle}</div>}
      </div>
      {first ? (
        <div className="absolute -left-[2.2px] top-[21px] z-0 h-full border-l border-dashed border-gray-300 dark:border-gray-700" />
      ) : last ? (
        <div className="absolute -left-[2.2px] top-[1px] z-0 h-[10px] border-l border-dashed border-gray-300 dark:border-gray-700" />
      ) : (
        <div className="absolute -left-[2.2px] top-0 z-0 h-full border-l border-dashed border-gray-300 dark:border-gray-700" />
      )}
    </div>
  )
}

export const YouHaveSignedIcon = ({
  numMissingSignatures,
}: {
  numMissingSignatures: number
}) => {
  return (
    <Tooltip
      content={`You've signed. ${numMissingSignatures} more ${plural(
        'signature',
        numMissingSignatures,
      )} needed.`}
    >
      <div className="flex items-center space-x-2 text-green-500">
        <Signature className="w-5" />
      </div>
    </Tooltip>
  )
}

export const HideTokenDropdownItem = ({
  smartAccountAddress,
  token,
  chainId,
  children,
  assetType,
  nftSymbol,
  nftTokenId,
}: {
  // Absent for watched-address (card) transfers, which have no smart account
  smartAccountAddress?: IAddress
  token: EvmAddress.Type
  chainId: SupportedChainId
  children?: React.ReactNode
  assetType: AssetType
  nftSymbol?: string
  nftTokenId?: string
}) => {
  const isNativeToken = isNativeTokenAddress(token)
  const { setGlobalModalState } = useAuth()

  return (
    <DropdownMenuItem
      tooltip={
        isNativeToken ? 'Unable to report native token as spam' : undefined
      }
      disabled={isNativeToken}
      onClick={() => {
        setGlobalModalState({
          isOpen: true,
          content: (
            <HideTokenDialogContent
              smartAccountAddress={smartAccountAddress}
              setIsOpen={() => {
                setGlobalModalState({
                  isOpen: false,
                  content: null,
                })
              }}
              token={token}
              chainId={chainId}
              assetType={assetType}
              nftSymbol={nftSymbol}
              nftTokenId={nftTokenId}
            />
          ),
        })
      }}
    >
      {children}
    </DropdownMenuItem>
  )
}
